import { useEffect, useRef, useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { getFileHash } from '@/components/helpers/getFileHash';
import { isFileProtected } from '@/components/helpers/isFileProtected';
import { isValidFile } from '@/components/helpers/isValidFile';
import { useStepper } from '@/features/v2/lib/stepper';

import { useLingui } from '@lingui/react';
import {
  useAbortItem,
  useBatchAddListener,
  useChunkFinishListener,
  useRequestPreSend,
} from '@rpldy/chunked-uploady';
import { useRetry } from '@rpldy/retry-hooks';
import UploadButton, { UploadButtonProps } from '@rpldy/upload-button';
import {
  useBatchFinishListener,
  useItemAbortListener,
  useItemErrorListener,
  useItemFinishListener,
  useItemStartListener,
  useUploady,
} from '@rpldy/uploady';

interface SortedFiles {
  validFiles: FileData[];
  invalidFiles: FileData[];
}

export type HashedFile = File & { sha: string };

export type StatusFile =
  | 'pending'
  | 'added'
  | 'uploading'
  | 'cancelled'
  | 'finished'
  | 'error'
  | 'aborted';

export interface FileData {
  id: string;
  name: string;
  password?: string | null;
  downloadUrl: string;
  locked?: boolean;
  size: number;
  //
  status: StatusFile;
  progress: number;
  file?: File;
  errorMessage?: string;
  passwordProtected?: boolean;
}

export const STATUS_FILE = {
  PENDING: 'pending',
  ADDED: 'added',
  UPLOADING: 'uploading',
  CANCELLED: 'cancelled',
  FINISHED: 'finished',
  ERROR: 'error',
  ABORTED: 'aborted',
} as const satisfies Record<Uppercase<StatusFile>, StatusFile>;

const DUPLICATE_FILE_STATUS = 400;

// chunked uploads resolve their error without a top level status,
// the failing chunk response carries it instead
const getUploadErrorStatus = (item: {
  uploadStatus?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadResponse?: any;
}): number | undefined =>
  item.uploadResponse?.chunkUploadResponse?.status || item.uploadStatus;

export type HandleDeleteFile = (item: FileData) => void;
export type HandleRetry = (id: string) => void;
export type HandleUpdatePassword = (
  id: string,
  password: string | null
) => void;

async function sortFiles(array: FileData[]): Promise<SortedFiles> {
  const sortedFiles: SortedFiles = {
    validFiles: [],
    invalidFiles: [],
  };

  for (const item of array) {
    if (!item.file) {
      sortedFiles.validFiles.push(item);
      continue;
    }
    const isPassword = await isFileProtected(item.file);
    if (isValidFile(item.file).isValid) {
      sortedFiles.validFiles.push({
        ...item,
        passwordProtected: isPassword,
      });
    } else {
      sortedFiles.invalidFiles.push(item);
    }
  }

  return sortedFiles;
}

export const useHelpfindUploady = ({
  defaultValue,
  onSuccess,
  onFinish,
}: {
  defaultValue?: FileData[];
  multiple?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (data: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFinish?: (data: any) => void;
}) => {
  const ref = useRef<null | FileData[]>(null);
  const [files, setFiles] = useState<FileData[]>(defaultValue || []);
  const abortItem = useAbortItem();

  const retry = useRetry();
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const { i18n } = useLingui();
  const uploader = useUploady();
  const { setForceDisableButton } = useStepper();

  // sync async data with uploader
  useEffect(() => {
    if (
      files.find((f) => f.status === STATUS_FILE.ADDED) &&
      setForceDisableButton
    ) {
      setForceDisableButton(true);
    }

    if (
      defaultValue &&
      (defaultValue.length === 0 || defaultValue.some((file) => file.name)) &&
      JSON.stringify(defaultValue) !== JSON.stringify(ref.current)
    ) {
      ref.current = defaultValue;
      if (files.length === 0)
        setFiles(
          defaultValue.filter(
            (item) => !files.find((f) => f.name === item.name)
          )
        );
      // if (defaultValue.length > files.length)
      setFiles([
        ...files,
        ...defaultValue.filter(
          (item) => !files.find((f) => f.name === item.name)
        ),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, ref]);

  useRequestPreSend(({ items }) => {
    return {
      options: {
        params: {
          sha: (items[0].file as HashedFile).sha,
          resumableIdentifier: crypto
            .getRandomValues(new Uint32Array(2))
            .join('-'),
        },
      },
    };
  });

  useBatchAddListener(async ({ items }) => {
    setError('');
    const newFiles = items.map((item) => {
      return {
        id: item.id,
        file: item.file as File,
        progress: 0,
        status: STATUS_FILE.ADDED,
        name: item.file.name,
        size: item.file.size,
        downloadUrl: '',
      } as FileData;
    });

    const filteredFiles = newFiles.filter(
      (newFiles) =>
        !files.some(
          (item) => item.name === newFiles.name && item.size === newFiles.size
        )
    );
    const array: FileData[] = [...files, ...filteredFiles];
    const sortedFiles = await sortFiles(array);

    if (sortedFiles.invalidFiles.length > 0) {
      if (
        sortedFiles.validFiles.some((item) => item.status === STATUS_FILE.ADDED)
      )
        setError(i18n.t('Część plików jest niepoprawna i nie została dodana.'));
      if (
        sortedFiles.invalidFiles.some(
          (item) => !isValidFile(item.file!).validType
        )
      ) {
        setError(
          i18n.t(
            'Plik niepoprawny, preferowane rozszerzenia to JPG, PNG, HEIC, PDF, ZIP lub 7ZIP.'
          )
        );
      }
      if (
        sortedFiles.invalidFiles.some(
          (item) => !isValidFile(item.file!).validSize
        )
      ) {
        setError(
          i18n.t(
            'Rozmiar pliku przekracza dopuszczalny limit, maksymalna waga to 25 MB.'
          )
        );
      }
      sortedFiles.invalidFiles.forEach((item) => abortItem(item.id));
    }

    setFiles(sortedFiles.validFiles);

    for await (const item of items) {
      await getFileHash(item.file as File).then((res) => {
        (item.file as HashedFile).sha = res;
      });
    }

    uploader.processPending();
  });

  useItemStartListener((item) => {
    const isDuplicate = files.some(
      (file) =>
        file.name === item.file.name &&
        file.size === item.file.size &&
        file.status !== item.state
    );
    if (isDuplicate) {
      setWarning(
        i18n.t(
          'Ten plik jest już załączony do Twojej sprawy. Dziękujemy. W razie wątpliwości prosimy o kontakt.'
        )
      );
    } else {
      setWarning('');
    }

    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const linkParam = searchParams.get('link');

      if (linkParam === 'exitpopup') {
        const hasExitPopupEvent =
          Array.isArray(window.dataLayer) &&
          window.dataLayer.some(
            (dlItem) =>
              dlItem.event === 'exit_popup' &&
              dlItem.popup_action === 'documents_uploaded' &&
              dlItem.popup_name === 'exit_popup_lp_parabanki'
          );

        if (!hasExitPopupEvent) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'exit_popup',
            popup_action: 'documents_uploaded',
            popup_name: 'exit_popup_lp_parabanki',
          });
        }
      }
    }

    return !isDuplicate;
  });
  useChunkFinishListener((data) => {
    setFiles((prev: FileData[]) =>
      prev.map((item: FileData) =>
        item.name === data.item.file.name
          ? {
              ...item,
              status: STATUS_FILE.UPLOADING,
              progress: data.item.completed,
            }
          : item
      )
    );
  });

  useItemErrorListener((item) => {
    if (getUploadErrorStatus(item) === DUPLICATE_FILE_STATUS) {
      setError(
        i18n.t(
          'Ten plik został już wgrany i wykryty jako duplikat. Usuń go i dodaj inny plik.'
        )
      );
    } else if (
      files.some(
        (file) =>
          file.name === item.file.name && file.status === STATUS_FILE.ERROR
      )
    )
      setError(
        i18n.t(
          'Wygląda na to, że błąd się powtarza. Spróbuj ponownie, a jeśli problem nadal będzie występował, skontaktuj się z nami.'
        )
      );
    setFiles((prev: FileData[]) =>
      prev.map((f: FileData) =>
        f.name === item.file.name ? { ...f, status: STATUS_FILE.ERROR } : f
      )
    );
  });

  useItemFinishListener((item) => {
    const uploadResponse =
      item.uploadResponse.results[item.uploadResponse.results.length - 1].data;

    setFiles((prev: FileData[]) =>
      prev.map((f: FileData) => {
        if (
          f.name === item.file.name &&
          f.size === item.file.size &&
          f.status !== STATUS_FILE.ERROR
        ) {
          return {
            ...f,
            status: STATUS_FILE.FINISHED,
            progress: 100,
          };
        }
        return f;
      })
    );

    if (uploadResponse)
      setFiles((prev: FileData[]) =>
        prev.map((f: FileData) => {
          if (f.name === item.file.name && f.size === item.file.size)
            return {
              ...f,
              id: uploadResponse.id,
              name: uploadResponse.name,
              size: uploadResponse.size,
              downloadUrl: uploadResponse.download_url,
              locked: !uploadResponse.can_be_managed,
            };
          return f;
        })
      );
  });

  useBatchFinishListener((batch) => {
    setForceDisableButton && setForceDisableButton(false);

    const commonItems = batch.items.filter((batchItem) =>
      files?.some(
        (file) =>
          file.name === batchItem.file.name && file.size === batchItem.file.size
      )
    );

    if (!files.some((f) => f.status === STATUS_FILE.ERROR)) {
      setFiles((prev: FileData[]) =>
        prev.map((f: FileData) =>
          commonItems.some((item) => item.file.name === f.name)
            ? {
                ...f,
                status: STATUS_FILE.FINISHED,
                progress: 100,
              }
            : f
        )
      );
      onSuccess && !warning && !error && onSuccess(files);
    }

    onFinish && onFinish(files);
  });
  const handleDeleteFile: HandleDeleteFile = useDebouncedCallback(
    (item: FileData) => {
      setError('');
      setWarning('');
      if (
        item.status === STATUS_FILE.FINISHED ||
        item.status === STATUS_FILE.ERROR
      ) {
        setFiles((prev) =>
          prev.length > 0 ? prev.filter((f) => f.id !== item.id) : []
        );
        return;
      }
      abortItem(item.id);
    },
    200
  );

  const handleRetry: HandleRetry = (id: string) => {
    retry(id);
    setError('');
    if (files) setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpdatePassword: HandleUpdatePassword = (
    id: string,
    password: string | null
  ) => {
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id === id)
          return {
            ...f,
            password,
          };
        return f;
      })
    );
  };
  useItemAbortListener((item) => {
    setFiles((prev) =>
      prev.length > 0 ? prev.filter((f) => f.id !== item.id) : []
    );
  });
  return {
    files,
    handleDeleteFile,
    handleRetry,
    handleUpdatePassword,
    error,
    warning,
    clearError: () => setError(''),
    clearWarning: () => setWarning(''),
    clearFiles: () => setFiles([]),
    ButtonFile: ({ ...rest }: UploadButtonProps<HTMLButtonElement>) => (
      <UploadButton
        {...rest}
        extraProps={{
          type: 'button',
          ...rest.extraProps,
        }}
      />
    ),
  };
};
